import { useMemo, type ReactNode } from "react";
import { trpc } from "@/core/api";
import { FeatureFlagContext, useFeatureFlags, type FeatureFlagContextValue } from "@webtemplate/ui";

// The context object + pure consumers (useFeatureFlags/useFeatureFlag/FeatureGate)
// now live in @webtemplate/ui (D-20). This module keeps only the tRPC-backed
// FeatureFlagProvider. Re-exported for back-compat with existing @/core imports
// (incl. the test helpers that mount FeatureFlagContext directly).
export { FeatureFlagContext, useFeatureFlags };
export type { FeatureFlagContextValue };

interface FeatureFlagProviderProps {
  /** Feature flag keys to preload */
  flagKeys: string[];
  children: ReactNode;
}

/**
 * Provider that preloads feature flag evaluations for a set of keys.
 *
 * Uses tRPC's useQueries hook to fire all evaluate queries in a single
 * hook call, respecting the Rules of Hooks. httpBatchLink automatically
 * batches the individual queries into a single HTTP request.
 */
export function FeatureFlagProvider({ flagKeys, children }: FeatureFlagProviderProps) {
  // Use tRPC's useQueries to evaluate all flag keys in a single hook call.
  // This avoids calling useQuery inside .map() which violates Rules of Hooks.
  const queries = trpc.useQueries((t) =>
    flagKeys.map((key) =>
      t.featureFlags.evaluate(
        { key },
        {
          staleTime: 30_000,
          refetchOnWindowFocus: false,
        },
      ),
    ),
  );

  const isLoading = queries.some((q) => q.isLoading);

  // Build a stable string key from query results to memoize the enabled set.
  // This avoids the spread-in-deps pattern while still recomputing only when
  // actual flag evaluation results change.
  const queryResultKey = queries.map((q) => q.data?.enabled ?? "pending").join(",");
  const flagKeysKey = flagKeys.join(",");

  // Build enabled flags set from query results. queryResultKey and flagKeysKey
  // are stable string serializations of the query results and flagKeys array
  // that change only when their underlying values change. Using them as deps
  // gives useMemo a fixed-length array while still reacting to real changes.
  // This is equivalent to deps=[queries, flagKeys] but avoids re-running when
  // parent re-creates the flagKeys array reference or tRPC returns a new queries
  // array reference with identical data.
  const enabledFlags = useMemo(() => {
    const set = new Set<string>();
    const keys = flagKeysKey.split(",").filter(Boolean);
    const results = queryResultKey.split(",");
    for (let i = 0; i < keys.length; i++) {
      if (results[i] === "true") {
        set.add(keys[i]!);
      }
    }
    return set;
  }, [queryResultKey, flagKeysKey]);

  const value = useMemo<FeatureFlagContextValue>(
    () => ({
      enabledFlags,
      isLoading,
      isEnabled: (key: string) => enabledFlags.has(key),
    }),
    [enabledFlags, isLoading],
  );

  return <FeatureFlagContext.Provider value={value}>{children}</FeatureFlagContext.Provider>;
}
